import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Search, X, Filter, LayoutGrid, List } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useOwnerStore } from '@/lib/owner-store';
import { cn } from '@/lib/utils';
import { TaskPriority, TaskStatus } from '@/lib/types';
import { TaskFormDialog } from './task-form-dialog';
import { DateRangePicker } from '@/components/ui/date-range-picker';

interface TaskBoardToolbarProps {
  onSearchChange: (value: string) => void;
  onFilterChange: (filters: any) => void;
  viewMode: 'list' | 'kanban';
  onViewModeChange: (mode: 'list' | 'kanban') => void;
}

export function TaskBoardToolbar({ 
  onSearchChange, 
  onFilterChange,
  viewMode,
  onViewModeChange
}: TaskBoardToolbarProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const { owners, fetchOwners } = useOwnerStore();
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | ''>('');
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority | ''>('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onSearchChange(value);
  };

  const handleStatusChange = (status: TaskStatus | '') => {
    setSelectedStatus(status);
    onFilterChange({ status });
  };

  const handlePriorityChange = (priority: TaskPriority | '') => {
    setSelectedPriority(priority);
    onFilterChange({ priority });
  };

  const handleOwnerChange = (owner: string) => {
    setSelectedOwner(owner);
    onFilterChange({ owner });
  };

  const handleDateRangeChange = (range: { start: string; end: string }) => {
    setDateRange(range);
    onFilterChange({ dateRange: range });
  };

  const clearFilters = () => {
    setSelectedStatus('');
    setSelectedPriority('');
    setSelectedOwner('');
    setDateRange({ start: '', end: '' });
    setSearch('');
    onFilterChange({
      status: '',
      priority: '',
      owner: '',
      dateRange: { start: '', end: '' }
    });
    onSearchChange('');
  };

  const hasActiveFilters = selectedStatus || selectedPriority || selectedOwner || dateRange.start || dateRange.end || search;

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center justify-between gap-4">
        <TaskFormDialog />
        
        <div className="flex-1 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search tasks..."
              className="pl-9"
            />
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "relative",
              showFilters && "bg-gray-100 dark:bg-gray-700"
            )}
          >
            <Filter className="h-4 w-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
            )}
          </Button>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-700 pl-4">
          <Button
            variant={viewMode === 'kanban' ? 'default' : 'ghost'}
            size="icon"
            onClick={() => onViewModeChange('kanban')}
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="icon"
            onClick={() => onViewModeChange('list')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <select
            value={selectedStatus}
            onChange={(e) => handleStatusChange(e.target.value as TaskStatus | '')}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Statuses</option>
            <option value="Open">Open</option>
            <option value="Active">Active</option>
            <option value="Hold">Hold</option>
            <option value="Closed">Closed</option>
          </select>

          <select
            value={selectedPriority}
            onChange={(e) => handlePriorityChange(e.target.value as TaskPriority | '')}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Priorities</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>

          <select
            value={selectedOwner}
            onChange={(e) => handleOwnerChange(e.target.value)}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Owners</option>
            {owners.map(owner => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>

          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </div>
      )}
    </div>
  );
}
